import { Component, OnInit, OnDestroy, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MeetingsService } from '../meetings.service';
import { SwalUtils } from '../../../../shared/utils/swal.utils';

export interface MeetingDetail {
  meeting_id: number;
  meeting_title: string;
  meeting_date?: string;
  meeting_lieu?: string;
  meeting_category?: string;
  meeting_participants?: string;
  meeting_compte_rendu?: string;
  meeting_status: 'ACTI' | 'INAC';
  meeting_status_label?: string;
  photo?: string;
}

@Component({
  selector: 'app-meetings-detail',
  standalone: false,
  templateUrl: './meetings-detail.component.html',
  styleUrls: ['./meetings-detail.component.scss']
})
export class MeetingsDetailComponent implements OnInit, OnDestroy {
  meeting = signal<MeetingDetail | null>(null);
  isLoading = signal(true);
  errorMessage = signal<string | null>(null);
  pendingStatus = signal<{ status: 'ACTI' | 'INAC' } | null>(null);
  pendingDelete = signal(false);
  isSaving = signal(false);

  private destroy$ = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private meetingsService: MeetingsService
  ) {}

  ngOnInit(): void {
    const id = Number(this.route.snapshot.paramMap.get('id'));
    this.meetingsService.getMeetingById(id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.meeting.set(res.data || res);
          this.isLoading.set(false);
        },
        error: () => {
          this.errorMessage.set('Impossible de charger cette réunion');
          this.isLoading.set(false);
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  goBack(): void { this.router.navigate(['/federation/reunions']); }
  onEdit(): void { this.router.navigate(['/federation/reunions', this.meeting()!.meeting_id, 'edit']); }

  confirmStatus(status: 'ACTI' | 'INAC'): void { this.pendingStatus.set({ status }); }
  cancelStatus(): void { this.pendingStatus.set(null); }

  applyStatus(): void {
    const m = this.meeting();
    const pending = this.pendingStatus();
    if (!m || !pending) return;
    this.isSaving.set(true);
    this.pendingStatus.set(null);
    this.meetingsService.setStatus(m.meeting_id, pending.status)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.meeting.update(prev => prev ? { ...prev, meeting_status: pending.status } : prev);
          this.isSaving.set(false);
          const label = pending.status === 'ACTI' ? 'En ligne' : 'Hors ligne';
          SwalUtils.success('Statut mis à jour', `La réunion est maintenant ${label}.`);
        },
        error: () => {
          SwalUtils.error('Erreur', 'Impossible de mettre à jour le statut.');
          this.isSaving.set(false);
        }
      });
  }

  confirmDelete(): void { this.pendingDelete.set(true); }
  cancelDelete(): void { this.pendingDelete.set(false); }

  applyDelete(): void {
    const m = this.meeting();
    if (!m) return;
    this.isSaving.set(true);
    this.pendingDelete.set(false);
    this.meetingsService.setStatus(m.meeting_id, 'ARCH')
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => this.router.navigate(['/federation/reunions']),
        error: () => {
          SwalUtils.error('Erreur', 'Impossible d\'archiver cette réunion.');
          this.isSaving.set(false);
        }
      });
  }

  statusLabel(status: string): string {
    return ({ ACTI: 'En ligne', INAC: 'Hors ligne' } as Record<string, string>)[status] || status;
  }

  statusBadgeClass(status: string): string {
    if (status === 'ACTI') return 'bg-emerald-50 text-emerald-600 border-emerald-100';
    return 'bg-amber-50 text-amber-600 border-amber-100';
  }

  formatDate(dateStr: string): string {
    if (!dateStr) return '—';
    try {
      return new Date(dateStr).toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric' });
    } catch { return dateStr; }
  }
}
